import { CreateBusinessData, BusinessAddress } from '../../business';

interface ContactInfoSectionProps {
  formData: CreateBusinessData;
  errors: Record<string, string>;
  onChange: (field: keyof CreateBusinessData, value: string) => void;
  onAddressChange: (field: keyof BusinessAddress, value: string) => void;
  onPhoneBlur?: () => void;
  onPostalCodeBlur?: () => void;
}

/**
 * ContactInfoSection component for collecting email, phone, and address information
 */
export function ContactInfoSection({ formData, errors, onChange, onAddressChange, onPhoneBlur, onPostalCodeBlur }: ContactInfoSectionProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            id="email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => onChange('email', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
              errors.email ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="maria@sparkleClean.com"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'email-error' : undefined}
          />
          {errors.email && (
            <p id="email-error" className="text-red-600 text-sm mt-1" role="alert">
              {errors.email}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone <span className="text-red-500">*</span>
          </label>
          <input
            id="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={(e) => onChange('phone', e.target.value)}
            onBlur={onPhoneBlur}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
              errors.phone ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="(555) 123-4567"
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? 'phone-error' : undefined}
          />
          {errors.phone && (
            <p id="phone-error" className="text-red-600 text-sm mt-1" role="alert">
              {errors.phone}
            </p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Address <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              id="addressLine1"
              type="text"
              placeholder="Street Address"
              required
              value={formData.address.addressLine1}
              onChange={(e) => onAddressChange('addressLine1', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                errors.addressLine1 ? 'border-red-300' : 'border-gray-300'
              }`}
              aria-invalid={!!errors.addressLine1}
              aria-describedby={errors.addressLine1 ? 'addressLine1-error' : undefined}
            />
            <input
              id="addressLine2"
              type="text"
              placeholder="Apt/Suite (Optional)"
              value={formData.address.addressLine2}
              onChange={(e) => onAddressChange('addressLine2', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <input
              id="addressCity"
              type="text"
              placeholder="City"
              required
              value={formData.address.addressCity}
              onChange={(e) => onAddressChange('addressCity', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                errors.addressCity ? 'border-red-300' : 'border-gray-300'
              }`}
              aria-invalid={!!errors.addressCity}
              aria-describedby={errors.addressCity ? 'addressCity-error' : undefined}
            />
            <input
              id="addressState"
              type="text"
              placeholder="State"
              required
              value={formData.address.addressState}
              onChange={(e) => onAddressChange('addressState', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                errors.addressState ? 'border-red-300' : 'border-gray-300'
              }`}
              aria-invalid={!!errors.addressState}
              aria-describedby={errors.addressState ? 'addressState-error' : undefined}
            />
            <input
              id="addressPostalCode"
              type="text"
              placeholder="Postal Code"
              required
              value={formData.address.addressPostalCode}
              onChange={(e) => onAddressChange('addressPostalCode', e.target.value)}
              onBlur={onPostalCodeBlur}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
                errors.addressPostalCode ? 'border-red-300' : 'border-gray-300'
              }`}
              aria-invalid={!!errors.addressPostalCode}
              aria-describedby={errors.addressPostalCode ? 'addressPostalCode-error' : undefined}
            />
          </div>
          {(errors.addressLine1 || errors.addressCity || errors.addressState || errors.addressPostalCode) && (
            <div className="mt-2 space-y-1">
              {errors.addressLine1 && (
                <p id="addressLine1-error" className="text-red-600 text-sm" role="alert">
                  {errors.addressLine1}
                </p>
              )}
              {errors.addressCity && (
                <p id="addressCity-error" className="text-red-600 text-sm" role="alert">
                  {errors.addressCity}
                </p>
              )}
              {errors.addressState && (
                <p id="addressState-error" className="text-red-600 text-sm" role="alert">
                  {errors.addressState}
                </p>
              )}
              {errors.addressPostalCode && (
                <p id="addressPostalCode-error" className="text-red-600 text-sm" role="alert">
                  {errors.addressPostalCode}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
